/**
 * Factory for frozen ice-block buttons.
 * Returns a <button> styled via .ice-button class, with an animated drip element
 * and a GSAP "drip" timeline that runs every ~3 seconds.
 */
import { gsap } from 'gsap'

export type IceButtonVariant = 'primary' | 'frost' | 'ghost' | 'cyan'
export type IceButtonSize = 'small' | 'default' | 'large'

export interface IceButtonOptions {
  label: string
  icon?: string
  variant?: IceButtonVariant
  size?: IceButtonSize
  onClick?: (e: MouseEvent) => void
  href?: string
  ariaLabel?: string
}

export function createIceButton(opts: IceButtonOptions): HTMLButtonElement | HTMLAnchorElement {
  const isLink = Boolean(opts.href)
  const el = document.createElement(isLink ? 'a' : 'button') as HTMLButtonElement & HTMLAnchorElement

  el.className = 'ice-button'
  if (opts.variant) el.classList.add(opts.variant)
  if (opts.size) el.classList.add(opts.size)

  if (isLink) {
    ;(el as HTMLAnchorElement).href = opts.href!
  } else {
    ;(el as HTMLButtonElement).type = 'button'
  }

  if (opts.ariaLabel) el.setAttribute('aria-label', opts.ariaLabel)

  if (opts.icon) {
    const icon = document.createElement('span')
    icon.className = 'ice-button__icon'
    icon.setAttribute('aria-hidden', 'true')
    icon.textContent = opts.icon
    el.appendChild(icon)
  }

  const label = document.createElement('span')
  label.className = 'ice-button__label'
  label.textContent = opts.label
  el.appendChild(label)

  // Drip element — absolutely-positioned pseudo. We animate its height/opacity with GSAP.
  const drip = document.createElement('span')
  drip.className = 'drip'
  drip.setAttribute('aria-hidden', 'true')
  el.appendChild(drip)

  if (opts.onClick) {
    el.addEventListener('click', (e) => {
      opts.onClick!(e)
    })
  }

  // Periodic drip — loops a GSAP timeline: drop grows from 0 to 14 px, then fades.
  // Randomized delay so multiple buttons don't drip in sync.
  const dripTl = gsap.timeline({ repeat: -1, delay: 1 + Math.random() * 3 })
  dripTl
    .set(drip, { height: 0, opacity: 0 })
    .to(drip, { opacity: 1, height: 4, duration: 0.18, ease: 'power1.out' })
    .to(drip, { height: 14, duration: 0.45, ease: 'power2.in' })
    .to(drip, { opacity: 0, duration: 0.25 }, '-=0.18')
    .to({}, { duration: 2.5 + Math.random() * 2.5 }) // pause between drips

  // Clean up drip timeline if the button is removed from DOM
  const observer = new MutationObserver(() => {
    if (!document.body.contains(el)) {
      dripTl.kill()
      observer.disconnect()
    }
  })
  observer.observe(document.body, { childList: true, subtree: true })

  return el
}
